import { FieldHelperProps, FieldInputProps, FieldMetaProps } from 'formik';
import useCurrencies from 'hooks/useCurrencies';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeCurrenciesToSelect } from '../selects/utils';
import FormSelect, { GroupSelectOption, SelectOption } from './formSelect/DeclarationSelect';
import { SelectProps } from 'antd';
import useCodelists from '../../../../hooks/useCodelists';
import useFormUtils from '../../../../hooks/useFormUtils';
import { TestProps } from 'core/utils/testTypes';
import config from 'config/config';

const CURRENCIES: Record<string, { id: string; value: string }> = {
    GBP: {
        id: 'GBP',
        value: 'Sterling',
    },
    EUR: {
        id: 'EUR',
        value: 'Euro',
    },
    USD: {
        id: 'USD',
        value: 'US Dollar',
    },
};

export interface CurrencyProps extends SelectProps, TestProps {
    refNumber?: string;
    label?: string;
    fieldProps: FieldInputProps<any>;
    fieldMeta?: FieldMetaProps<any>;
    fieldHelper?: FieldHelperProps<any>;
    required?: boolean;
    viewOnly?: boolean;
    disabled?: boolean;
    tooltip?: string;
    refClicked?: (refNumber: string | number) => void;
    condensed?: boolean;
    hidden?: boolean;
    selectOptions?: Readonly<SelectOption[]> | Readonly<GroupSelectOption>;
}

/**
 * @argument selectOptions -  used only for AES
 */
const CurrencySelect: FC<CurrencyProps> = ({
    fieldProps,
    refNumber,
    label,
    fieldMeta,
    required,
    fieldHelper,
    viewOnly,
    disabled,
    tooltip,
    refClicked,
    condensed,
    testId,
    selectOptions,
    ...selectProps
}) => {
    const { t } = useTranslation('form');
    const { currencies } = useCurrencies();
    const { aisCodelists, cdsCodelists, aesCodelists } = useCodelists();

    const { isAis, isCds, isAes } = useFormUtils();

    // Note: Probably we should always use the codelists for the options (not only for prod or AIS)
    // For now, I'm using only for AIS
    const currencyCodelist = useMemo(() => {
        if (config.isProduction) {
            if (selectOptions) return selectOptions;
            else if (isAis) {
                return aisCodelists?.currencies;
            } else if (isCds) {
                return cdsCodelists?.currencyTypes;
            } else if (isAes) {
                return aesCodelists?.invoiceCurrency;
            } else {
                return normalizeCurrenciesToSelect(currencies.list);
            }
        } else {
            if (isCds) {
                return [
                    { id: CURRENCIES.USD.id, value: CURRENCIES.USD.value },
                    { id: CURRENCIES.EUR.id, value: CURRENCIES.EUR.value },
                    { id: CURRENCIES.GBP.id, value: CURRENCIES.GBP.value },
                ];
            } else if (isAis) {
                return aisCodelists?.currencies.filter((curr) =>
                    [CURRENCIES.EUR.id, CURRENCIES.GBP.id, CURRENCIES.USD.id].includes(curr.id)
                );
            } else {
                return [
                    { id: CURRENCIES.USD.id, value: CURRENCIES.USD.value },
                    { id: CURRENCIES.EUR.id, value: CURRENCIES.EUR.value },
                ];
            }
        }
    }, [
        selectOptions,
        currencies.list,
        aisCodelists?.currencies,
        cdsCodelists?.currencyTypes,
        isAis,
        isCds,
        isAes,
        aesCodelists?.invoiceCurrency,
    ]);

    return (
        <FormSelect
            disabled={disabled}
            viewOnly={viewOnly}
            required={required}
            refNumber={refNumber}
            label={t(label || 'currency')}
            selectOptions={currencyCodelist}
            fieldProps={fieldProps}
            fieldMeta={fieldMeta}
            fieldHelper={fieldHelper}
            refClicked={refClicked}
            tooltip={tooltip}
            condensed={condensed}
            testId={testId}
            hideCodelistMenu
            {...selectProps}
        />
    );
};

export default CurrencySelect;
